import { useEffect, useState } from 'react';
import { canViewAdvisors, canWriteAdvisors } from './advisors';
import { fetch_me as fetchMe } from '../../sales_team/api';
import type { SalesMe } from '../../sales_team/api';

export type AdvisorAccess = 'loading' | 'allowed' | 'denied';

export interface AdvisorPermissions {
  // View gate: SUPER_USER or can_edit_team; a failed /me denies a non-super-user.
  access: AdvisorAccess;
  // Write gate: can_write_advisors; false until /me says yes, so write controls never flash.
  canWrite: boolean;
}

// /me is asked for super users too: viewing does not imply writing.
// 'loading' lets a page wait for /me instead of flashing the no-access card.
export function useAdvisorAccess(isSuperUser: boolean): AdvisorPermissions {
  const [me, setMe] = useState<SalesMe | null>(null);
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    let live = true;
    fetchMe()
      .then((m) => live && setMe(m))
      .catch(() => live && setMe(null))
      .finally(() => live && setSettled(true));
    return () => {
      live = false;
    };
  }, []);
  if (canViewAdvisors(isSuperUser, me))
    return { access: 'allowed', canWrite: canWriteAdvisors(me) };
  return { access: settled ? 'denied' : 'loading', canWrite: false };
}

// Boolean form for controls that simply stay hidden until /me says yes.
function useCanManageAdvisors(isSuperUser: boolean): { canView: boolean; canWrite: boolean } {
  const { access, canWrite } = useAdvisorAccess(isSuperUser);
  return { canView: access === 'allowed', canWrite };
}

export default useCanManageAdvisors;
